import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../components/Theme";
import GetOperationById from "../../api/operation/GetOperationById";
import DeleteOperationById from "../../api/operation/DeleteOperationById";
import CreateChemicalOperation from "../../api/operation/CreateChemicalOperation";

export default function Chemical({ appCropId }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [chemicals, setChemicals] = useState([]);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  useEffect(() => {
    const fetchData = async () => {
      if (appCropId) {
        try {
          const res = await GetOperationById({ appCropId, appOperId: "OC" });
          setChemicals(res);
        } catch (err) {
          console.error("Error fetching chemical data:", err);
        }
      }
    };
    fetchData();
  }, [appCropId]);
  const handleChange = (index, key, value) => {
    const updated = [...chemicals];
    updated[index][key] = value;
    setChemicals(updated);
  };
  const handleAdd = () => {
    const timestamp = Math.floor(Date.now() / 1000);
    setChemicals([
      ...chemicals,
      {
        appCropId,
        appOperId: "OC" + timestamp,
        operDate: "",
        substance: "",
        commercialName: "",
        unitUsed: "ลิตร",
        chemicalAmount: 0,
        waterAmount: 0,
        reason: "",
      },
    ]);
  };

  const openDeleteDialog = (id) => {
    setSelectedDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await DeleteOperationById({ appOperId: selectedDeleteId });
      setChemicals((prev) =>
        prev.filter((item) => item.appOperId !== selectedDeleteId)
      );
      setSnackbarSeverity("success");
      setSnackbarMessage("ลบรายการสำเร็จ");
    } catch (err) {
      setSnackbarSeverity("error");
      setSnackbarMessage("เกิดข้อผิดพลาดในการลบ");
    } finally {
      setDeleteDialogOpen(false);
      setOpenSnackbar(true);
    }
  };

  const handleSave = async () => {
    const results = await CreateChemicalOperation(chemicals);
    const hasError = results.some((r) => r.status !== 200);
    setSnackbarSeverity(hasError ? "error" : "success");
    setSnackbarMessage(
      hasError ? "บางรายการบันทึกไม่สำเร็จ" : "บันทึกสำเร็จทั้งหมด"
    );
    setOpenSnackbar(true);
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box p={2}>
      <Box
        width="100%"
        sx={{ backgroundColor: colors.greenAccent[300] }}
        display="flex"
        justifyContent="center"
      >
        <Typography
          p={isMobile ? 1 : 2}
          variant={isMobile ? "body" : "h6"}
          fontWeight="bold"
          color="#fff"
        >
          ตารางการใช้สารเคมี
        </Typography>
      </Box>

      <Box maxWidth={isMobile ? "320px" : "none"}>
        <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>วันที่</TableCell>
                <TableCell>ชื่อสาร</TableCell>
                <TableCell>ชื่อการค้า</TableCell>
                <TableCell>หน่วย</TableCell>
                <TableCell>ปริมาณสาร</TableCell>
                <TableCell>น้ำ (ลิตร)</TableCell>
                <TableCell>เหตุผล</TableCell>
                <TableCell align="center">ลบ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chemicals.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      size="small"
                      value={row.operDate}
                      onChange={(e) =>
                        handleChange(index, "operDate", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={row.substance}
                      onChange={(e) =>
                        handleChange(index, "substance", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={row.commercialName}
                      onChange={(e) =>
                        handleChange(index, "commercialName", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      fullWidth
                      value={row.unitUsed}
                      onChange={(e) =>
                        handleChange(index, "unitUsed", e.target.value)
                      }
                    >
                      <MenuItem value="ลิตร">ลิตร</MenuItem>
                      <MenuItem value="มิลลิลิตร">มิลลิลิตร</MenuItem>
                      <MenuItem value="กิโลกรัม">กิโลกรัม</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={row.chemicalAmount}
                      onChange={(e) =>
                        handleChange(index, "chemicalAmount", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={row.waterAmount}
                      onChange={(e) =>
                        handleChange(index, "waterAmount", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={row.reason}
                      onChange={(e) =>
                        handleChange(index, "reason", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="error"
                      onClick={() => openDeleteDialog(row.appOperId)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    variant="outlined"
                  >
                    เพิ่มแถว
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box textAlign="center" mt={3}>
        <Button
          variant="contained"
          sx={{ backgroundColor: colors.greenAccent[400] }}
          onClick={handleSave}
        >
          บันทึกข้อมูล
        </Button>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            justifyContent: "center",
            minWidth: "200px",
            textAlign: "center",
          },
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>คุณต้องการลบรายการนี้ใช่หรือไม่?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>ยกเลิก</Button>
          <Button color="error" onClick={confirmDelete}>
            ลบ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
